import { createFileRoute, useRouter } from "@tanstack/react-router";
import { useServerFn } from "@tanstack/react-start";
import { useQuery } from "@tanstack/react-query";
import { Fragment, useMemo, useState } from "react";
import { Loader2, ShieldCheck, RefreshCw, Download, Search, ChevronDown, ChevronRight } from "lucide-react";
import { listAuditLog } from "@/lib/admin.functions";

const KNOWN_ACTIONS = ["settings.update", "role.grant", "role.revoke"];

export const Route = createFileRoute("/_authenticated/admin/audit")({
  head: () => ({ meta: [{ title: "Audit Log — Admin" }] }),
  component: AuditLog,
  errorComponent: ({ error, reset }) => {
    const router = useRouter();
    return (
      <div className="py-16 text-center">
        <p className="text-red-400 mb-3 text-sm">{error.message}</p>
        <button onClick={() => { router.invalidate(); reset(); }} className="bg-white text-zinc-950 px-5 py-2 rounded-full text-sm font-semibold">Try again</button>
      </div>
    );
  },
  notFoundComponent: () => <div className="text-zinc-400">Not found.</div>,
});

function AuditLog() {
  const fn = useServerFn(listAuditLog);
  const [action, setAction] = useState<string>("");
  const [query, setQuery] = useState("");
  const [expanded, setExpanded] = useState<Record<string, boolean>>({});
  const { data: rows = [], isLoading, isFetching, refetch } = useQuery({
    queryKey: ["admin", "audit", action],
    queryFn: () => fn({ data: { action: action || undefined, limit: 300 } }),
  });

  const actionOptions = useMemo(() => {
    const set = new Set<string>(KNOWN_ACTIONS);
    for (const r of rows as any[]) if (r?.action) set.add(r.action);
    return ["", ...Array.from(set).sort()];
  }, [rows]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows as any[];
    return (rows as any[]).filter((r) => {
      const hay = [
        r.actor_email, r.actor_id, r.action, r.target_type, r.target_id,
        r.details ? JSON.stringify(r.details) : "",
      ].filter(Boolean).join(" ").toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  function exportCsv() {
    const header = ["created_at", "actor_email", "actor_id", "action", "target_type", "target_id", "details"];
    const escape = (v: any) => {
      const s = v == null ? "" : typeof v === "object" ? JSON.stringify(v) : String(v);
      return `"${s.replace(/"/g, '""')}"`;
    };
    const lines = [header.join(",")];
    for (const r of filtered) lines.push(header.map((h) => escape((r as any)[h])).join(","));
    const blob = new Blob([lines.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `audit-log-${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
  }

  return (
    <div className="space-y-6 max-w-6xl">
      <header>
        <p className="text-amber-300 text-xs font-semibold tracking-widest uppercase inline-flex items-center gap-1.5">
          <ShieldCheck className="size-3.5" /> Audit Trail
        </p>
        <h1 className="font-serif text-3xl text-white mt-1">Admin Audit Log</h1>
        <p className="text-zinc-500 text-sm mt-1">Every settings change and role grant/revoke is recorded here.</p>
      </header>

      <div className="flex flex-wrap gap-2 items-center">
        <select value={action} onChange={(e) => setAction(e.target.value)} className="px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white">
          {actionOptions.map((a) => <option key={a || "all"} value={a}>{a || "All actions"}</option>)}
        </select>
        <div className="relative flex-1 min-w-[200px] max-w-md">
          <Search className="size-4 absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
          <input
            value={query} onChange={(e) => setQuery(e.target.value)}
            placeholder="Search actor, target, details…"
            className="w-full pl-9 pr-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-white placeholder:text-zinc-500"
          />
        </div>
        <button onClick={() => refetch()} disabled={isFetching}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-white/5 border border-white/10 rounded-lg text-zinc-200 hover:bg-white/10 disabled:opacity-50">
          <RefreshCw className={`size-4 ${isFetching ? "animate-spin" : ""}`} /> Refresh
        </button>
        <button onClick={exportCsv} disabled={!filtered.length}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-sm bg-amber-300/10 border border-amber-300/30 rounded-lg text-amber-200 hover:bg-amber-300/20 disabled:opacity-40">
          <Download className="size-4" /> Export CSV
        </button>
        <span className="text-xs text-zinc-500 ml-auto">{filtered.length} of {rows.length} events</span>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        {isLoading ? (
          <div className="py-16 grid place-items-center"><Loader2 className="size-5 animate-spin text-zinc-500" /></div>
        ) : filtered.length === 0 ? (
          <div className="py-16 text-center text-zinc-500 text-sm">No audit events recorded yet.</div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-white/[0.02] text-zinc-500 text-xs uppercase tracking-wider">
              <tr>
                <th className="w-8" />
                <th className="text-left px-4 py-3">When</th>
                <th className="text-left px-4 py-3">Actor</th>
                <th className="text-left px-4 py-3">Action</th>
                <th className="text-left px-4 py-3">Target</th>
                <th className="text-left px-4 py-3">Details</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((r: any) => {
                const hasDetails = r.details && Object.keys(r.details).length > 0;
                const isOpen = !!expanded[r.id];
                return (
                  <Fragment key={r.id}>
                    <tr key={r.id} className="border-t border-white/5 align-top hover:bg-white/[0.02] cursor-pointer"
                        onClick={() => hasDetails && setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}>
                      <td className="px-2 py-3 text-zinc-500">
                        {hasDetails ? (isOpen ? <ChevronDown className="size-4" /> : <ChevronRight className="size-4" />) : null}
                      </td>
                      <td className="px-4 py-3 text-zinc-400 text-xs whitespace-nowrap">{new Date(r.created_at).toLocaleString()}</td>
                      <td className="px-4 py-3 text-zinc-300 text-xs">{r.actor_email ?? r.actor_id?.slice(0, 8) ?? "—"}</td>
                      <td className="px-4 py-3"><span className="text-[11px] px-2 py-1 rounded-full bg-amber-300/10 border border-amber-300/30 text-amber-200">{r.action}</span></td>
                      <td className="px-4 py-3 text-zinc-400 text-xs">{r.target_type ?? "—"}{r.target_id ? ` · ${String(r.target_id).slice(0, 12)}` : ""}</td>
                      <td className="px-4 py-3 text-zinc-500 text-[11px] font-mono max-w-md truncate">
                        {hasDetails ? JSON.stringify(r.details) : "—"}
                      </td>
                    </tr>
                    {isOpen && hasDetails && (
                      <tr key={`${r.id}-d`} className="border-t border-white/5 bg-black/40">
                        <td />
                        <td colSpan={5} className="px-4 py-3">
                          <pre className="text-[11px] font-mono text-zinc-300 whitespace-pre-wrap break-words">{JSON.stringify(r.details, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}